import { FC, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { createColumnHelper, ColumnDef } from '@tanstack/react-table';
import {
  Link as CarbonLink,
  Tag,
  DatePicker,
  DatePickerInput,
  InlineNotification,
} from '@carbon/react';
import { useAtom } from 'jotai';
import routes from '~/routes';
import { PageHeader } from 'components/page-header';
import { DataTable } from 'components/data-table';
import { formatNumber } from 'utils/number';
import { agentCostsAtom, dateRangeAtom } from './atoms';
import { AgentCostCharts } from './charts';
import { CostComparison } from './cost-comparison';
import { KpiCards, KpiCardsSkeleton } from './kpi-cards';
import { fromDateParam, toDateParam } from 'utils/dates';
import { formatCost, inProgressCount, unpricedMessage } from './format';
import type { AgentCostSeries, AgentCostSummary, UnpricedModel } from './types';
import styles from './page.module.scss';

const EMPTY_SERIES: AgentCostSeries = {
  dates: [],
  runsByStatus: [],
  costByAgent: [],
  failuresByAgent: [],
};

// Module-level so its identity is stable across renders (the `unpricedNotice`
// memo below depends on `unpricedModels`); a fresh `[]` each render would defeat it.
const EMPTY_UNPRICED: UnpricedModel[] = [];

const detailPath = (agentType: string): string =>
  routes.adminAgentCostDetailPath.replace(
    ':agentType',
    encodeURIComponent(agentType)
  );

const DateRangeFilter: FC = () => {
  const [range, setRange] = useAtom(dateRangeAtom);
  return (
    <DatePicker
      datePickerType="range"
      dateFormat="m/d/Y"
      value={[fromDateParam(range.start), fromDateParam(range.end)]}
      onChange={(dates: Date[]) => {
        if (dates.length === 2) {
          setRange({
            start: toDateParam(dates[0]),
            end: toDateParam(dates[1]),
          });
        }
      }}
    >
      <DatePickerInput
        id="agent-cost-range-start"
        labelText="From"
        placeholder="mm/dd/yyyy"
        size="sm"
      />
      <DatePickerInput
        id="agent-cost-range-end"
        labelText="To"
        placeholder="mm/dd/yyyy"
        size="sm"
      />
    </DatePicker>
  );
};

const columnHelper = createColumnHelper<AgentCostSummary>();

const buildColumns = (onOpen: (agentType: string) => void) => [
  columnHelper.accessor('agentName', {
    id: 'agentName',
    header: 'Agent',
    size: 260,
    meta: { type: 'text' },
    cell: (info) => {
      const { agentType } = info.row.original;
      return (
        <CarbonLink
          href={detailPath(agentType)}
          className={styles.link}
          onClick={(e: React.MouseEvent) => {
            e.preventDefault();
            onOpen(agentType);
          }}
        >
          {info.getValue()}
        </CarbonLink>
      );
    },
  }),
  columnHelper.accessor('totalRuns', {
    id: 'totalRuns',
    header: 'Total Runs',
    size: 120,
    meta: { type: 'integer' },
  }),
  columnHelper.accessor('totalSuccess', {
    id: 'totalSuccess',
    header: 'Success',
    size: 120,
    enableColumnFilter: false,
    cell: (info) => (
      <Tag type="green" size="sm">
        {formatNumber({
          value: info.getValue(),
          minPrecision: 0,
          includeCommas: true,
        })}
      </Tag>
    ),
  }),
  columnHelper.accessor('totalFailed', {
    id: 'totalFailed',
    header: 'Failed',
    size: 120,
    enableColumnFilter: false,
    cell: (info) => {
      const value = info.getValue();
      return value > 0 ? (
        <Tag type="red" size="sm">
          {formatNumber({ value, minPrecision: 0, includeCommas: true })}
        </Tag>
      ) : (
        <span className={styles.zeroCount}>0</span>
      );
    },
  }),
  // Derived so the row reconciles by construction:
  // Total Runs = Success + Failed + In Progress (pending/processing runs).
  // Mirrors the In Progress KPI card (shared helper in ./format).
  columnHelper.accessor((row) => inProgressCount(row), {
    id: 'inProgress',
    header: 'In Progress',
    size: 120,
    enableColumnFilter: false,
    meta: { type: 'integer' },
    cell: (info) => {
      const value = info.getValue() as number;
      return value > 0 ? (
        <Tag type="purple" size="sm">
          {formatNumber({ value, minPrecision: 0, includeCommas: true })}
        </Tag>
      ) : (
        <span className={styles.zeroCount}>0</span>
      );
    },
  }),
  columnHelper.accessor('totalTokens', {
    id: 'totalTokens',
    header: 'Total Tokens',
    size: 140,
    enableColumnFilter: false,
    meta: { type: 'integer' },
  }),
  columnHelper.accessor('totalCost', {
    id: 'totalCost',
    header: 'Total Cost',
    size: 140,
    meta: { type: 'currency' },
    cell: (info) => <strong>{formatCost(info.getValue())}</strong>,
  }),
  columnHelper.accessor('avgDurationSeconds', {
    id: 'avgDurationSeconds',
    header: 'Avg Duration',
    size: 130,
    enableColumnFilter: false,
    meta: { type: 'duration' },
  }),
  columnHelper.accessor('p95DurationSeconds', {
    id: 'p95DurationSeconds',
    header: 'P95 Duration',
    size: 130,
    enableColumnFilter: false,
    meta: { type: 'duration' },
  }),
];

export const AgentCostsPage: FC = () => {
  const history = useHistory();
  const [loadableState] = useAtom(agentCostsAtom);

  const summary =
    loadableState.state === 'hasData' ? loadableState.data : undefined;

  const isLoading = loadableState.state === 'loading';
  const loadError =
    loadableState.state === 'hasError'
      ? loadableState.error instanceof Error
        ? loadableState.error.message
        : 'Failed to fetch agent costs'
      : null;

  const data = summary?.data ?? [];
  const totals = summary?.stats;
  const series = summary?.series;
  const models = summary?.modelBreakdown ?? [];
  const generatedAt = summary?.generatedAt;
  const unpricedModels = summary?.unpricedModels ?? EMPTY_UNPRICED;

  const columns = useMemo(
    () => buildColumns((agentType) => history.push(detailPath(agentType))),
    [history]
  );

  // Warn when some runs used models missing from the price list — their cost is
  // reported as $0 and would otherwise read as "free" rather than "unknown".
  // Carbon's InlineNotification subtitle accepts a plain string only.
  const unpricedNotice = useMemo(() => {
    if (unpricedModels.length === 0) return null;
    const totalRuns = unpricedModels.reduce((sum, m) => sum + m.runs, 0);
    return unpricedMessage({ models: unpricedModels, runCount: totalRuns });
  }, [unpricedModels]);

  return (
    <>
      <PageHeader
        breadcrumbs={[]}
        title="Agent Cost"
        subtitle="Anthropic agent runs — cost, volume, and success rate by agent type."
        actions={<DateRangeFilter />}
      />
      {unpricedNotice && (
        <InlineNotification
          kind="warning"
          lowContrast
          title="Some models are unpriced"
          subtitle={unpricedNotice}
          hideCloseButton
        />
      )}
      {isLoading ? (
        <KpiCardsSkeleton />
      ) : (
        totals && (
          <KpiCards
            totals={totals}
            agents={data}
            series={series ?? EMPTY_SERIES}
            generatedAt={generatedAt}
            unpricedModels={unpricedModels}
          />
        )
      )}
      <div className={styles.tableSection}>
        <DataTable
          data={data}
          columns={columns as ColumnDef<AgentCostSummary>[]}
          getRowId={(row) => row.agentType}
          isLoading={isLoading}
          error={loadError}
          persistenceKey="agent-costs-summary"
          initialTableState={{
            sortColumn: 'totalCost',
            sortDirection: 'desc',
          }}
          config={{
            columnResizing: true,
            sorting: true,
            globalSearch: true,
            columnFilters: true,
            // Intentionally NOT fitContent: with only 8 narrow columns the table
            // would leave a large gap on the right of wide screens. Letting the
            // extra width distribute across the columns fills the container.
            export: {
              exportPresets: [
                { name: 'Export', format: 'csv', filename: 'agent-costs' },
              ],
            },
          }}
        />
      </div>
      <CostComparison />
      {(series || data.length > 0) && (
        <AgentCostCharts
          series={series ?? EMPTY_SERIES}
          agents={data}
          models={models}
          onSelectAgent={(agentType) => history.push(detailPath(agentType))}
        />
      )}
    </>
  );
};
